import { Component, OnInit, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { getDashboardUrl } from '../../shared/runtime-config';
import { readStoredAuthToken } from '../../shared/auth.service';
import { formatPhoneNumber } from '../../shared/phone-format';

interface LeadDetailRow {
  displayName: string;
  phoneNumber: string;
  status: string;
  handledBy: string;
}

type UnknownMap = Record<string, unknown>;

@Component({
  standalone: true,
  selector: 'app-leads-page',
  imports: [CommonModule],
  template: `
  <div class="page">
    <div class="hero">
      <div class="ic">✨</div>
      <div>
        <div class="h1">Leads</div>
        <div class="h2">Lead details from backend endpoint</div>
      </div>
    </div>

    <div class="syncMsg" *ngIf="loading()">Loading lead details...</div>
    <div class="syncMsg" *ngIf="!loading() && error()">{{ error() }}</div>

    <div class="stats">
      <div class="stat">
        <div class="k">Total Leads</div>
        <div class="v">{{ leads().length }}</div>
      </div>
      <div class="stat">
        <div class="k">Handled By Human</div>
        <div class="v">{{ humanCount() }}</div>
      </div>
      <div class="stat">
        <div class="k">Handled By Bot</div>
        <div class="v">{{ botCount() }}</div>
      </div>
    </div>

    <div class="table card">
      <div class="head">
        <div>Display Name</div>
        <div>Phone Number</div>
        <div>Status</div>
        <div>Handled By</div>
      </div>

      <div class="row" *ngFor="let lead of leads()">
        <div class="name">{{ lead.displayName }}</div>
        <div class="muted">{{ formatPhone(lead.phoneNumber) }}</div>
        <div><span class="status">{{ lead.status }}</span></div>
        <div class="muted">{{ lead.handledBy }}</div>
      </div>

      <div class="empty" *ngIf="!loading() && !leads().length">No lead details found.</div>
    </div>
  </div>
  `,
  styles: [`
    .page{flex:1; padding:16px; overflow:auto}
    .hero{display:flex; gap:12px; align-items:center; padding:14px; border-radius:18px;
      border:1px solid var(--border); background:rgba(17,27,33,.55)}
    .ic{width:44px;height:44px;border-radius:16px;display:flex;align-items:center;justify-content:center;
      background:rgba(0,168,132,.16);border:1px solid rgba(0,168,132,.35);font-size:20px}
    .h1{font-size:18px;font-weight:900}
    .h2{margin-top:2px;color:var(--muted);font-size:12px}
    .syncMsg{margin-top:10px;font-size:12px;color:var(--muted)}
    .stats{margin-top:12px;display:grid;gap:12px;grid-template-columns:repeat(3,minmax(0,1fr))}
    .stat{padding:12px;border-radius:16px;border:1px solid var(--border);background:rgba(17,27,33,.55)}
    .k{font-size:12px;color:var(--muted)}
    .v{font-size:24px;font-weight:900;margin-top:4px}
    .card{margin-top:12px;padding:10px;border-radius:18px;border:1px solid var(--border);background:rgba(17,27,33,.55)}
    .table .head,.table .row{display:grid;grid-template-columns:1.5fr 1.2fr 1fr 1fr;gap:10px;align-items:center}
    .table .head{padding:8px 10px;font-size:11px;color:var(--muted);font-weight:700;text-transform:uppercase;letter-spacing:.4px}
    .table .row{padding:10px;border-radius:12px}
    .table .row:hover{background:rgba(42,57,66,.35)}
    .name{font-weight:800}
    .status{font-size:11px;padding:3px 8px;border-radius:999px;border:1px solid rgba(34,197,94,.35);background:rgba(34,197,94,.14);color:rgb(134,239,172);text-transform:capitalize}
    .muted{color:var(--muted)}
    .empty{padding:12px;color:var(--muted);text-align:center}
    :host-context(body.light) .hero,
    :host-context(body.light) .stat,
    :host-context(body.light) .card{
      background:rgba(255,255,255,.92);
      border-color:rgba(15,23,42,.10);
    }
    :host-context(body.light) .table .row:hover{background:rgba(15,23,42,.04)}
    :host-context(body.light) .h1,
    :host-context(body.light) .name,
    :host-context(body.light) .v{color:#0f172a}
    :host-context(body.light) .k,
    :host-context(body.light) .h2,
    :host-context(body.light) .muted,
    :host-context(body.light) .empty,
    :host-context(body.light) .table .head{color:#475569}
    :host-context(body.light) .status{
      color:#166534;
      border-color:rgba(22,163,74,.35);
      background:rgba(34,197,94,.16);
    }
    @media (max-width: 900px){
      .stats{grid-template-columns:1fr}
      .table .head{display:none}
      .table .row{grid-template-columns:1fr;gap:6px}
    }
  `]
})
export class LeadsPageComponent implements OnInit {
  readonly loading = signal(false);
  readonly error = signal('');
  readonly leads = signal<LeadDetailRow[]>([]);

  readonly humanCount = computed(() =>
    this.leads().filter(item => item.handledBy.toLowerCase() === 'human').length
  );
  readonly botCount = computed(() =>
    this.leads().filter(item => item.handledBy.toLowerCase() === 'bot').length
  );

  constructor(private readonly http: HttpClient) {}

  async ngOnInit(): Promise<void> {
    await this.loadLeadDetails();
  }

  private async loadLeadDetails(): Promise<void> {
    this.loading.set(true);
    this.error.set('');
    try {
      const token = readStoredAuthToken();
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const endpoint = `${getDashboardUrl()}/api/webapp/lead-details`;
      const payload = await firstValueFrom(
        this.http.get<unknown>(endpoint, { headers: new HttpHeaders(headers) })
      );

      this.leads.set(this.extractRows(payload));
    } catch (error) {
      console.error('Failed to load lead details', error);
      this.error.set('Failed to load lead details.');
      this.leads.set([]);
    } finally {
      this.loading.set(false);
    }
  }

  private extractRows(payload: unknown): LeadDetailRow[] {
    const rows = this.readRows(payload);
    return rows
      .map(row => this.mapLead(row))
      .filter((row): row is LeadDetailRow => !!row);
  }

  private readRows(payload: unknown): unknown[] {
    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== 'object') return [];
    const source = payload as UnknownMap;
    if (Array.isArray(source['data'])) return source['data'] as unknown[];
    if (Array.isArray(source['items'])) return source['items'] as unknown[];
    if (Array.isArray(source['leads'])) return source['leads'] as unknown[];
    return [];
  }

  private mapLead(row: unknown): LeadDetailRow | null {
    if (!row || typeof row !== 'object') return null;
    const source = row as UnknownMap;

    const displayName = this.readString(source, ['displayName', 'name', 'contactLabel']) || 'Unknown';
    const phoneNumber = this.readString(source, ['phoneNumber', 'phone', 'to']) || '-';
    const status = this.readString(source, ['status', 'leadStatus', 'stage']) || '-';
    const handledBy = this.readString(source, ['handledBy', 'owner', 'assignee']) || '-';

    return { displayName, phoneNumber, status, handledBy };
  }

  formatPhone(value: string): string {
    return formatPhoneNumber(value);
  }

  private readString(source: UnknownMap, keys: string[]): string {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
      if (typeof value === 'number') return String(value);
    }
    return '';
  }
}
